import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import { useParams, useNavigate } from "react-router-dom";
import {
  clearValidationErrors,
  executeValidationWithArrays,
  handleArrayItemRemoveWithValidation,
  clearFieldError,
} from "../../../utils/formValidation";

const getBreadcrumbs = (studioId, studiosList) => {
  const baseBreadcrumbs = [
    { title: "Home", path: `/` },
    {
      title: "Reserve Confirm",
      path: `/reserve_confirm${studioId ? `/${studioId}` : ""}`,
    },
  ];

  if (studioId && studiosList.length > 0) {
    const studio = studiosList.find((s) => s.id === parseInt(studioId));
    if (studio) {
      baseBreadcrumbs.push({
        title: studio.name_ja,
        path: `/reserve_confirm/${studioId}`,
      });
    }
  }

  return baseBreadcrumbs;
};

export default function ReserveConfirmEditPage() {
  const contentKey = "reserveConfirmContent";
  const [message, setMessage] = useState("");
  const { studioId } = useParams();
  const navigate = useNavigate();
  const [selectedStudioId, setSelectedStudioId] = useState(null);
  const [studiosList, setStudiosList] = useState([]);
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_content: "",
    reserve_confirm_items: [],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handleReserveConfirmReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          reserve_confirm_items: reorderedItems.map((item, index) => ({
            ...item,
            display_order: index,
          })),
        };

        clearValidationErrors();

        // iframeに配列データを送信
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "UPDATE_ARRAY",
              contentKey,
              arrayKey: "reserve_confirm_items",
              arrayData: newData.reserve_confirm_items,
            },
            process.env.REACT_APP_ORIGIN
          );
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.reserve_confirm_items || [],
    handleReserveConfirmReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    if (!studioId) {
      setPageContent({
        page_content: "",
        reserve_confirm_items: [],
      });
      setLoadingText("");
      return;
    }

    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_reserve_confirm_content/?studio_id=${studioId}`
      );
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("reserve confirm data", data);
      const newData = {
        page_content: data.page_content || "",
        reserve_confirm_items: data.reserve_confirm_items || [],
      };
      setPageContent(newData);
      setLoadingText("");
      return newData;
    } catch (error) {
      console.error("データ取得エラー:", error);
      alert(`データの取得に失敗しました: ${error.message}`);
      setLoadingText("");
      return null;
    }
  }, [studioId]);

  // スタジオリストの取得
  const fetchStudiosList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_access_content/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data) setStudiosList(data.studios);
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
    }
  }, []);

  useEffect(() => {
    if (studioId) {
      fetchPageContentData();
    }
    fetchStudiosList();
  }, [studioId, fetchPageContentData, fetchStudiosList]);

  // バリデーションルールを定義
  const validationRules = {
    page_content: { required: true },
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    if (!studioId) {
      alert("スタジオを選択してください");
      return;
    }

    const arrayValidationRules = {
      reserve_confirm_items: {
        title: { required: true },
        content: { required: true },
      },
    };

    const fieldLabels = {
      page_content: "お礼文",
    };

    if (
      !executeValidationWithArrays(
        pageContent,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    // 送信用データの準備
    const formData = {
      studio_id: parseInt(studioId),
      page_content: {
        page_content: pageContent.page_content,
        reserve_confirm_items: pageContent.reserve_confirm_items.map(
          (item, index) => ({
            ...item,
            display_order: index,
          })
        ),
      },
    };

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_reserve_confirm_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "更新に失敗しました");

      setMessage(data.message || "正常に更新されました");
      await fetchPageContentData(); // 最新データ再取得

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      alert(`更新に失敗しました: ${error.message}`);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = async () => {
    clearValidationErrors();
    setMessage("");
    const freshData = await fetchPageContentData();

    // データ取得完了後にiframeに送信
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow && freshData) {
        // ページ本文を更新
        iframe.contentWindow.postMessage(
          {
            type: "UPDATE_TEXT",
            contentKey,
            fieldName: "page_content",
            value: freshData.page_content,
          },
          process.env.REACT_APP_ORIGIN
        );

        // 予約確認項目を更新
        iframe.contentWindow.postMessage(
          {
            type: "UPDATE_ARRAY",
            contentKey,
            arrayKey: "reserve_confirm_items",
            arrayData: freshData.reserve_confirm_items,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // ページ本文の入力変更
  const handlePageContentChange = (e) => {
    const { value } = e.target;
    const eventType = e.type;

    // エラークラスをクリア
    clearFieldError(e.target);

    setPageContent((prev) => {
      const newData = { ...prev, page_content: value };

      // iframeにテキストデータを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const messageType = eventType === "focus" ? "SCROLL_TO" : "UPDATE_TEXT";

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: ".edit_page_content",
            contentKey,
            fieldName: "page_content",
            value: value,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 予約確認項目の入力変更
  const handleReserveConfirmItemInputChange = (e, index, fieldName) => {
    const { value } = e.target;
    const eventType = e.type;

    // エラークラスをクリア
    clearFieldError(e.target);

    setPageContent((prev) => {
      const newData = {
        ...prev,
        reserve_confirm_items: prev.reserve_confirm_items.map((item, i) =>
          i === index ? { ...item, [fieldName]: value } : item
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_reserve_confirm_${index}_${fieldName}`;
        const messageType =
          eventType === "focus" ? "SCROLL_TO" : "UPDATE_ARRAY";

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            arrayKey: "reserve_confirm_items",
            arrayData: newData.reserve_confirm_items,
            fieldName,
            index,
            value,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 予約確認項目の追加
  const addReserveConfirmItem = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = {
        title: "新しいセクション",
        content: "ここに内容を入力してください",
        display_order: 0,
      };

      let newItems;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newItems = [
          ...prev.reserve_confirm_items.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.reserve_confirm_items.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newItems = [...prev.reserve_confirm_items, newItem];
      }

      // display_orderをインデックスに基づいて更新
      newItems = newItems.map((item, index) => ({
        ...item,
        display_order: index,
      }));

      const newData = {
        ...prev,
        reserve_confirm_items: newItems,
      };

      // iframeに配列データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "UPDATE_ARRAY",
            contentKey,
            arrayKey: "reserve_confirm_items",
            arrayData: newData.reserve_confirm_items,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 予約確認項目の削除
  const removeReserveConfirmItem = (index) => {
    handleArrayItemRemoveWithValidation(
      index,
      setPageContent,
      "reserve_confirm_items",
      { title: { required: true }, content: { required: true } },
      { page_content: "お礼文" },
      (newData) => {
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "UPDATE_ARRAY",
              contentKey,
              arrayKey: "reserve_confirm_items",
              arrayData: newData.reserve_confirm_items,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={getBreadcrumbs(studioId, studiosList)}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* スタジオリスト */}
              <div className="studio-list">
                <ul>
                  {studiosList.map((studio) => (
                    <li
                      key={studio.id}
                      className={
                        parseInt(studioId) === studio.id ? "active" : ""
                      }
                      onClick={() => {
                        navigate(`/reserve_confirm/${studio.id}`);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {studio.name_ja}
                    </li>
                  ))}
                </ul>
              </div>

              {/* ページ本文 */}
              <section>
                <div className="area2">
                  <textarea
                    className="input2"
                    name="page_content"
                    value={pageContent.page_content || ""}
                    onChange={handlePageContentChange}
                    onFocus={handlePageContentChange}
                    placeholder="お礼文を入力してください..."
                    rows={3}
                    data-label="お礼文"
                    data-required="true"
                  />
                </div>
              </section>

              {/* 予約確認項目 */}
              <section>
                <div className="area2">
                  {pageContent.reserve_confirm_items?.map(
                    (reserve_confirm_item, index) => (
                      <div
                        className="item"
                        key={reserve_confirm_item.id || index}
                        {...getDraggableProps(index)}
                      >
                        <div className="ctrl">
                          <p>{index + 1}</p>
                          <div className="icon-wrap">
                            <i
                              className="icon delete"
                              onClick={() => removeReserveConfirmItem(index)}
                              onMouseDown={(e) => e.stopPropagation()}
                              title="削除"
                              style={{ cursor: "pointer" }}
                            ></i>
                            <i
                              className="icon add"
                              onClick={() => addReserveConfirmItem(index)}
                              onMouseDown={(e) => e.stopPropagation()}
                              title="追加"
                              style={{ cursor: "pointer" }}
                            ></i>
                          </div>
                        </div>
                        <div
                          className="forms loop"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{ gridTemplateColumns: "auto" }}
                        >
                          {/* タイトル入力欄 */}
                          <input
                            type="text"
                            name={`reserve_confirm_item${index}_title`}
                            value={reserve_confirm_item.title || ""}
                            onChange={(e) =>
                              handleReserveConfirmItemInputChange(
                                e,
                                index,
                                "title"
                              )
                            }
                            onFocus={(e) =>
                              handleReserveConfirmItemInputChange(
                                e,
                                index,
                                "title"
                              )
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            placeholder="タイトル"
                            data-label="タイトル"
                            data-required="true"
                          />

                          {/* コンテンツ入力欄 */}
                          <textarea
                            className="input2"
                            name={`reserve_confirm_item${index}_content`}
                            value={reserve_confirm_item.content || ""}
                            onChange={(e) =>
                              handleReserveConfirmItemInputChange(
                                e,
                                index,
                                "content"
                              )
                            }
                            onFocus={(e) =>
                              handleReserveConfirmItemInputChange(
                                e,
                                index,
                                "content"
                              )
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            placeholder="内容を入力してください..."
                            rows={3}
                            data-label="内容"
                            data-required="true"
                          />
                        </div>
                      </div>
                    )
                  )}

                  {/* 新規追加ボタン */}
                  {pageContent.reserve_confirm_items?.length === 0 && (
                    <div className="item">
                      <div className="ctrl">
                        <div className="icon-wrap">
                          <i
                            className="icon add"
                            onClick={addReserveConfirmItem}
                            title="新規追加"
                          ></i>
                        </div>
                      </div>
                      <div className="forms">
                        <p style={{ color: "#fff", opacity: 0.7 }}>
                          項目を追加してください
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe
        path={`${process.env.REACT_APP_ORIGIN}/reserve_confirm/preview${
          studioId ? `?studio_id=${studioId}` : ""
        }`}
        data={pageContent}
        contentKey={contentKey}
      />
    </>
  );
}
